import React, { useState, useCallback } from 'react';
import { GraduationCap, ChevronDown, Lightbulb } from 'lucide-react';
import { cn } from '@/lib/utils';

// Box "Guida rapida" a tendina (verde chiaro) mostrato in cima a ogni sezione del CRM.
// Presentazionale: riceve una guida già risolta { title, intro, steps:[{h,p}], tip? }
// e una storageKey per ricordare aperto/chiuso della singola sezione.
export function SectionGuide({ guide, storageKey }) {
  const key = storageKey || 'sila_guide_default';

  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(key);
      return v === null ? true : v === '1';
    } catch {
      return true;
    }
  });

  const toggle = useCallback(() => {
    setOpen((o) => {
      const next = !o;
      try { localStorage.setItem(key, next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  }, [key]);

  if (!guide || !guide.steps || guide.steps.length === 0) return null;

  return (
    <section
      data-testid="section-guide"
      className={cn(
        'mb-6 overflow-hidden rounded-xl border transition-colors',
        'border-emerald-200 bg-emerald-50/80',
        'dark:border-emerald-900/50 dark:bg-emerald-950/30'
      )}
    >
      <button
        type="button"
        onClick={toggle}
        data-testid="section-guide-toggle"
        aria-expanded={open}
        className={cn(
          'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors',
          'hover:bg-emerald-100/70 dark:hover:bg-emerald-900/30',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50'
        )}
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
          <GraduationCap className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-emerald-900 dark:text-emerald-100">
            Guida rapida &middot; {guide.title}
          </span>
          <span className="block truncate text-xs text-emerald-700/80 dark:text-emerald-300/70">
            {open ? 'Come operare in questa sezione' : 'Tocca per aprire la guida di questa sezione'}
          </span>
        </span>
        <ChevronDown
          className={cn(
            'h-5 w-5 shrink-0 text-emerald-600 transition-transform duration-200 dark:text-emerald-400',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1" data-testid="section-guide-body">
          {guide.intro && (
            <p className="mb-3 text-sm leading-relaxed text-emerald-900/90 dark:text-emerald-100/90">
              {guide.intro}
            </p>
          )}
          <ol className="space-y-2.5">
            {guide.steps.map((s, i) => (
              <li key={i} className="flex gap-3" data-testid={`section-guide-step-${i}`}>
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-[11px] font-bold text-white dark:bg-emerald-500">
                  {i + 1}
                </span>
                <span className="text-sm leading-relaxed">
                  <span className="font-semibold text-emerald-900 dark:text-emerald-100">{s.h}. </span>
                  <span className="text-emerald-800/90 dark:text-emerald-200/80">{s.p}</span>
                </span>
              </li>
            ))}
          </ol>
          {guide.tip && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-emerald-100/70 px-3 py-2 dark:bg-emerald-900/30">
              <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <p className="text-xs leading-relaxed text-emerald-800 dark:text-emerald-200">{guide.tip}</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
